import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Search, CheckCircle2, Clock, TrendingDown, TrendingUp, FileText, ExternalLink } from 'lucide-react';
import { financeAPI, suppliersAPI, sitesAPI } from '@/lib/api';
import { formatCurrency, supplierShortcutLabel, matchesSearch } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import ManualReceptionDetailDialog from '@/components/ManualReceptionDetailDialog';
import OrderDetailDialog from '@/components/OrderDetailDialog';

const COMPANY_MATCHERS = {
    'Fuji Express': (name) => name.startsWith('FE '),
    'Concept Lab': (name) => name === 'Concept Lab',
    'My Food': (name) => name === 'My Food',
    'Boucherie': (name) => name === 'Boucherie',
};

const DEFAULT_SUPPLIER_SHORTCUTS = [
    'Vodelys',
    'Pro à Pro Surgelés',
    'MG Store',
    'Asie Caraibes',
    'Le Moulin',
    'S.A.P.Y.',
];

const PERIOD_PRESETS = {
    month: { label: 'Mois', days: 30 },
    '3months': { label: '3 mois', days: 90 },
    year: { label: '1 an', days: 365 },
};

const FR_MONTHS_SHORT = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];

// Return the 6 most recent calendar months as [{key:'2026-05', label:'Mai 2026', year, monthIdx}, ...]
// monthIdx is 0-based.
const getMonthShortcuts = () => {
    const result = [];
    const now = new Date();
    for (let i = 0; i < 6; i += 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        result.push({
            key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: `${FR_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`,
            year: d.getFullYear(),
            monthIdx: d.getMonth(),
        });
    }
    return result;
};

const getMonthRange = (year, monthIdx) => {
    const start = new Date(year, monthIdx, 1);
    const end = new Date(year, monthIdx + 1, 0); // last day of the month
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(start), end: fmt(end) };
};

const todayStr = () => new Date().toISOString().split('T')[0];
const daysAgoStr = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
};

const normalizeStr = (s) => (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

export default function FinancePage() {
    const navigate = useNavigate();
    const [previewOrderId, setPreviewOrderId] = useState(null);
    const [previewReceptionId, setPreviewReceptionId] = useState(null);
    const openInOrders = (it) => {
        if (it.type === 'manual_reception') {
            setPreviewReceptionId(it.id);
        } else if (it.type === 'order') {
            setPreviewOrderId(it.id);
        } else {
            // Fallback for legacy types — navigate to the Orders page
            const param = 'openOrder';
            navigate(`/orders?${param}=${encodeURIComponent(it.id)}`);
        }
    };
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [status, setStatus] = useState('a_regler');
    const [filterCompany, setFilterCompany] = useState('all');
    const [filterSite, setFilterSite] = useState('all');
    const [filterSupplier, setFilterSupplier] = useState('all');
    const [filterPeriod, setFilterPeriod] = useState('month');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [search, setSearch] = useState('');

    // Reference data
    const [allSuppliers, setAllSuppliers] = useState([]);  // all en_compte suppliers
    const [sites, setSites] = useState([]);

    // Editable supplier shortcuts (persisted per browser)
    const [supplierShortcutNames, setSupplierShortcutNames] = useState(() => {
        try {
            const raw = localStorage.getItem('financePage_supplierShortcuts');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch { /* ignore */ }
        return DEFAULT_SUPPLIER_SHORTCUTS;
    });
    const [showShortcutEditor, setShowShortcutEditor] = useState(false);
    useEffect(() => {
        try {
            localStorage.setItem('financePage_supplierShortcuts', JSON.stringify(supplierShortcutNames));
        } catch { /* ignore */ }
    }, [supplierShortcutNames]);

    // Payment dialog
    const [paymentDialog, setPaymentDialog] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [activePaymentDate, setActivePaymentDate] = useState('');
    const [activePaymentNotes, setActivePaymentNotes] = useState('');
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);

    // Multi-selection for bulk payments
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());

    useEffect(() => {
        (async () => {
            try {
                const [sup, sit] = await Promise.all([
                    suppliersAPI.getAll(),
                    sitesAPI.getAll(),
                ]);
                setAllSuppliers((sup.data || []).filter(s => s.payment_mode === 'en_compte'));
                setSites(sit.data || []);
            } catch {
                // ignore
            }
        })();
    }, []);

    const resolveShortcutSupplier = (name) => {
        const n = normalizeStr(name);
        if (!n) return null;
        let match = allSuppliers.find(s => normalizeStr(s.name) === n);
        if (!match) match = allSuppliers.find(s => normalizeStr(s.name).startsWith(n));
        if (!match) match = allSuppliers.find(s => normalizeStr(s.name).includes(n));
        return match || null;
    };

    const getPeriodRange = () => {
        if (filterPeriod === 'custom') {
            return { start: customStart || null, end: customEnd || null };
        }
        // Calendar month shortcut: "month-YYYY-MM"
        if (filterPeriod.startsWith('month-')) {
            const [, year, mon] = filterPeriod.split('-');
            return getMonthRange(parseInt(year, 10), parseInt(mon, 10) - 1);
        }
        const preset = PERIOD_PRESETS[filterPeriod];
        if (!preset) return { start: null, end: null };
        return { start: daysAgoStr(preset.days), end: todayStr() };
    };

    const loadItems = async () => {
        setLoading(true);
        try {
            const { start, end } = getPeriodRange();
            const params = {};
            if (status && status !== 'all') params.status = status;
            if (filterSupplier && filterSupplier !== 'all') params.supplier_id = filterSupplier;
            if (filterSite && filterSite !== 'all') params.site_id = filterSite;
            if (start) params.start_date = start;
            if (end) params.end_date = end;
            const res = await financeAPI.list(params);
            setItems(res.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, filterSupplier, filterSite, filterPeriod, customStart, customEnd]);

    // Company filter is applied client-side (1-to-many site_ids per company)
    const getSiteIdsForCompany = (companyName) => {
        const matcher = COMPANY_MATCHERS[companyName];
        if (!matcher) return [];
        return sites.filter(s => matcher(s.name)).map(s => s.id);
    };

    const filteredItems = useMemo(() => {
        let list = items;
        if (filterCompany !== 'all') {
            const ids = new Set(getSiteIdsForCompany(filterCompany));
            list = list.filter(it => ids.has(it.site_id));
        }
        const q = search.trim();
        if (q) {
            list = list.filter(it =>
                matchesSearch(it.number, q) ||
                matchesSearch(it.supplier_name, q) ||
                matchesSearch(it.site_name, q) ||
                matchesSearch(it.document_number, q)
            );
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, filterCompany, search, sites]);

    const filteredTotals = useMemo(() => {
        const aReg = filteredItems.filter(it => it.payment_status === 'a_regler').reduce((s, it) => s + (it.total_amount || 0), 0);
        const reg = filteredItems.filter(it => it.payment_status === 'regle').reduce((s, it) => s + (it.total_amount || 0), 0);
        return { a_regler: aReg, regle: reg };
    }, [filteredItems]);

    const openMarkAsPaid = (item) => {
        setBulkMode(false);
        setActiveItem(item);
        setActivePaymentDate(item.payment_date || new Date().toISOString().slice(0, 10));
        setActivePaymentNotes(item.payment_notes || '');
        setPaymentDialog(true);
    };

    // --- Multi-selection helpers ---
    const itemKey = (it) => `${it.type}-${it.id}`;
    const unpaidFiltered = useMemo(() => filteredItems.filter(it => it.payment_status !== 'regle'), [filteredItems]);
    const selectedItems = useMemo(() => unpaidFiltered.filter(it => selectedKeys.has(itemKey(it))), [unpaidFiltered, selectedKeys]);
    const selectedTotal = useMemo(() => selectedItems.reduce((s, it) => s + (it.total_amount || 0), 0), [selectedItems]);
    const allUnpaidSelected = unpaidFiltered.length > 0 && selectedItems.length === unpaidFiltered.length;

    const toggleSelect = (it) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            const k = itemKey(it);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedKeys(allUnpaidSelected ? new Set() : new Set(unpaidFiltered.map(itemKey)));
    };

    const openBulkPayment = () => {
        if (selectedItems.length === 0) return;
        setBulkMode(true);
        setActiveItem(null);
        setActivePaymentDate(new Date().toISOString().slice(0, 10));
        setActivePaymentNotes('');
        setPaymentDialog(true);
    };

    const handleSavePayment = async () => {
        if (bulkMode) {
            if (selectedItems.length === 0) return;
            setBulkSaving(true);
            try {
                const results = await Promise.allSettled(selectedItems.map(it =>
                    financeAPI.updatePayment(it.type, it.id, {
                        payment_status: 'regle',
                        payment_date: activePaymentDate || null,
                        payment_notes: activePaymentNotes || null,
                    })
                ));
                const failed = results.filter(r => r.status === 'rejected').length;
                if (failed > 0) {
                    toast.error(`${failed} règlement(s) en échec sur ${selectedItems.length}`);
                } else {
                    toast.success(`${selectedItems.length} facture(s) marquée(s) réglée(s)`);
                }
                setPaymentDialog(false);
                setSelectedKeys(new Set());
                loadItems();
            } finally {
                setBulkSaving(false);
            }
            return;
        }
        if (!activeItem) return;
        try {
            await financeAPI.updatePayment(activeItem.type, activeItem.id, {
                payment_status: 'regle',
                payment_date: activePaymentDate || null,
                payment_notes: activePaymentNotes || null,
            });
            toast.success('Marqué comme réglé');
            setPaymentDialog(false);
            setActiveItem(null);
            loadItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const handleRevertToUnpaid = async (item) => {
        try {
            await financeAPI.updatePayment(item.type, item.id, {
                payment_status: 'a_regler',
                payment_date: null,
                payment_notes: null,
            });
            toast.success('Repassé en « à régler »');
            loadItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const resetFilters = () => {
        setStatus('a_regler');
        setFilterCompany('all');
        setFilterSite('all');
        setFilterSupplier('all');
        setFilterPeriod('month');
        setCustomStart('');
        setCustomEnd('');
        setSearch('');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Wallet className="w-6 h-6 text-emerald-600" />
                    Suivi financier
                </h1>
                <span className="text-sm text-slate-500">
                    Fournisseurs en compte uniquement
                </span>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="bg-red-100 rounded-lg p-2">
                            <TrendingDown className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">À régler</div>
                            <div className="text-2xl font-bold text-red-700 tabular-nums" data-testid="kpi-a-regler">
                                {formatCurrency(filteredTotals.a_regler)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="bg-emerald-100 rounded-lg p-2">
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Réglé</div>
                            <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="kpi-regle">
                                {formatCurrency(filteredTotals.regle)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="bg-slate-100 rounded-lg p-2">
                            <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Lignes</div>
                            <div className="text-2xl font-bold text-slate-900 tabular-nums">{filteredItems.length}</div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters — shortcuts row (mirrors Commandes) */}
            <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-8">
                <div className="flex flex-col gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg flex-1" data-testid="finance-shortcuts">
                    {/* Ligne 1 : entreprise */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Entreprise :</span>
                        {['all', ...Object.keys(COMPANY_MATCHERS)].map((company) => {
                            const isActive = filterCompany === company;
                            const label = company === 'all' ? 'Toutes' : company;
                            return (
                                <button
                                    key={company}
                                    type="button"
                                    onClick={() => {
                                        if (filterSite !== 'all') setFilterSite('all');
                                        setFilterCompany(company);
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                        isActive
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`finance-company-shortcut-${company}`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    {/* Ligne 2 : sites FE */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Sites FE :</span>
                        {['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin', 'Siège'].map((shortName) => {
                            const fullName = `FE ${shortName}`;
                            const site = sites.find(s => s.name === fullName);
                            const isActive = site && filterSite === site.id;
                            return (
                                <button
                                    key={shortName}
                                    type="button"
                                    disabled={!site}
                                    onClick={() => {
                                        if (filterCompany !== 'all') setFilterCompany('all');
                                        setFilterSite(isActive ? 'all' : site.id);
                                    }}
                                    className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                        !site
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-slate-900 text-white border-slate-900'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`finance-site-shortcut-${shortName.toLowerCase()}`}
                                >
                                    {shortName}
                                </button>
                            );
                        })}
                    </div>
                    {/* Ligne 3 : fournisseurs (editables, restreints aux en_compte) */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Fournisseurs :</span>
                        {supplierShortcutNames.map((name, idx) => {
                            const supplier = resolveShortcutSupplier(name);
                            const isActive = supplier && filterSupplier === supplier.id;
                            return (
                                <button
                                    key={`${name}-${idx}`}
                                    type="button"
                                    disabled={!supplier}
                                    onClick={() => supplier && setFilterSupplier(isActive ? 'all' : supplier.id)}
                                    className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                        !supplier
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                    }`}
                                    title={!supplier ? `Fournisseur non « en compte » ou introuvable : ${name}` : supplier.name}
                                    data-testid={`finance-supplier-shortcut-${normalizeStr(name)}`}
                                >
                                    {supplierShortcutLabel(supplier?.name || name)}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setShowShortcutEditor(true)}
                            className="px-2.5 py-1.5 rounded-full text-xs font-semibold text-slate-500 border border-dashed border-slate-300 hover:border-emerald-400 hover:text-emerald-600 flex-shrink-0"
                            title="Modifier la liste des raccourcis"
                            data-testid="finance-edit-supplier-shortcuts-btn"
                        >
                            Modifier
                        </button>
                    </div>
                </div>

                {/* Periode + statut */}
                <div className="flex flex-col gap-2 px-3 py-2 bg-emerald-50/50 border border-emerald-100 rounded-lg" data-testid="finance-period-block">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Période :</span>
                        {Object.entries(PERIOD_PRESETS).map(([key, preset]) => {
                            const isActive = filterPeriod === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setFilterPeriod(key)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                        isActive
                                            ? 'bg-emerald-500 text-white border-emerald-500'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                    }`}
                                    data-testid={`finance-period-shortcut-${key}`}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setFilterPeriod('custom')}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                filterPeriod === 'custom'
                                    ? 'bg-emerald-500 text-white border-emerald-500'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                            }`}
                            data-testid="finance-period-shortcut-custom"
                        >
                            Plage
                        </button>
                        {filterPeriod === 'custom' && (
                            <div className="flex items-center gap-1 ml-1">
                                <Input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="h-8 px-2 text-xs w-[125px]"
                                    data-testid="finance-period-custom-start"
                                />
                                <span className="text-slate-400 text-xs">→</span>
                                <Input
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="h-8 px-2 text-xs w-[125px]"
                                    data-testid="finance-period-custom-end"
                                />
                            </div>
                        )}
                    </div>
                    {/* Ligne raccourcis par mois calendaire (6 derniers mois) */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Mois :</span>
                        {getMonthShortcuts().map(({ key, label, year, monthIdx }) => {
                            const presetKey = `month-${key}`;
                            const isActive = filterPeriod === presetKey;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setFilterPeriod(presetKey)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                        isActive
                                            ? 'bg-emerald-500 text-white border-emerald-500'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                    }`}
                                    data-testid={`finance-month-shortcut-${key}`}
                                    title={`Du 1er ${FR_MONTHS_SHORT[monthIdx]} ${year} au dernier jour du mois`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Statut :</span>
                        {[
                            { key: 'a_regler', label: 'À régler' },
                            { key: 'regle', label: 'Réglé' },
                            { key: 'all', label: 'Tous' },
                        ].map(({ key, label }) => {
                            const isActive = status === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setStatus(key)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                        isActive
                                            ? (key === 'regle' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-500 text-white border-red-500')
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`finance-status-shortcut-${key}`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500">Recherche :</span>
                        <div className="relative flex-1">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <Input value={search} onChange={e => setSearch(e.target.value)} className="h-8 pl-7 text-xs" placeholder="N°, fournisseur, site..." data-testid="finance-search" />
                        </div>
                        <Button variant="outline" size="sm" onClick={resetFilters} className="h-8 text-xs" data-testid="finance-reset-filters">
                            Reset
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {selectedItems.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-emerald-50 border-b border-emerald-200" data-testid="finance-bulk-bar">
                            <span className="text-sm font-semibold text-emerald-800">
                                {selectedItems.length} facture(s) sélectionnée(s) — {formatCurrency(selectedTotal)}
                            </span>
                            <Button
                                size="sm"
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={openBulkPayment}
                                data-testid="finance-bulk-pay-btn"
                            >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Régler la sélection
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-slate-500"
                                onClick={() => setSelectedKeys(new Set())}
                                data-testid="finance-bulk-clear-btn"
                            >
                                Tout désélectionner
                            </Button>
                        </div>
                    )}
                    {loading ? (
                        <div className="p-10 text-center text-slate-400 text-sm">Chargement...</div>
                    ) : filteredItems.length === 0 ? (
                        <div className="p-10 text-center text-slate-400">
                            <Wallet className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                            Aucune ligne ne correspond aux filtres.
                            {allSuppliers.length === 0 && (
                                <div className="mt-2 text-xs">
                                    Aucun fournisseur configuré en compte. Allez dans Fournisseurs et définissez « Mode de règlement → En compte ».
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0 z-10">
                                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                                        <th className="px-3 py-2 w-8">
                                            <Checkbox
                                                checked={allUnpaidSelected}
                                                onCheckedChange={toggleSelectAll}
                                                disabled={unpaidFiltered.length === 0}
                                                aria-label="Tout sélectionner"
                                                data-testid="finance-select-all"
                                            />
                                        </th>
                                        <th className="px-3 py-2">Date</th>
                                        <th className="px-3 py-2">Type</th>
                                        <th className="px-3 py-2">N°</th>
                                        <th className="px-3 py-2">Fournisseur</th>
                                        <th className="px-3 py-2">Site</th>
                                        <th className="px-3 py-2">N° doc / BL</th>
                                        <th className="px-3 py-2 text-right">Montant</th>
                                        <th className="px-3 py-2 text-center">Statut</th>
                                        <th className="px-3 py-2">Réglé le</th>
                                        <th className="px-3 py-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map(it => {
                                        const isPaid = it.payment_status === 'regle';
                                        return (
                                            <tr
                                                key={`${it.type}-${it.id}`}
                                                className={`border-t ${isPaid ? 'bg-emerald-50/40' : 'bg-red-50/30'}`}
                                                data-testid={`finance-row-${it.type}-${it.id}`}
                                            >
                                                <td className="px-3 py-2 w-8">
                                                    {!isPaid && (
                                                        <Checkbox
                                                            checked={selectedKeys.has(`${it.type}-${it.id}`)}
                                                            onCheckedChange={() => toggleSelect(it)}
                                                            aria-label="Sélectionner"
                                                            data-testid={`finance-select-${it.type}-${it.id}`}
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{it.date || '—'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    {it.type === 'order' ? (
                                                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Commande</span>
                                                    ) : (
                                                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Réception</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => openInOrders(it)}
                                                        className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                                        title="Ouvrir dans Commandes"
                                                        data-testid={`finance-open-${it.type}-${it.id}`}
                                                    >
                                                        {it.number || '—'}
                                                        <ExternalLink className="w-3 h-3 opacity-60" />
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">{it.supplier_name}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.site_name}</td>
                                                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-500">{it.document_number || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(it.total_amount)}</td>
                                                <td className="px-3 py-2 text-center whitespace-nowrap">
                                                    {isPaid ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                                            <CheckCircle2 className="w-3 h-3" /> Réglé
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                                                            <Clock className="w-3 h-3" /> À régler
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-500">{it.payment_date || '—'}</td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    {isPaid ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-xs"
                                                            onClick={() => handleRevertToUnpaid(it)}
                                                            data-testid={`finance-revert-${it.type}-${it.id}`}
                                                        >
                                                            Annuler règlement
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
                                                            onClick={() => openMarkAsPaid(it)}
                                                            data-testid={`finance-mark-paid-${it.type}-${it.id}`}
                                                        >
                                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                                            Marquer réglé
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-slate-50 sticky bottom-0 border-t-2 border-slate-200">
                                    <tr>
                                        <td colSpan={7} className="px-3 py-2 text-right font-semibold text-slate-600">Totaux filtrés :</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="text-xs text-red-600 tabular-nums">À régler : {formatCurrency(filteredTotals.a_regler)}</div>
                                            <div className="text-xs text-emerald-600 tabular-nums">Réglé : {formatCurrency(filteredTotals.regle)}</div>
                                        </td>
                                        <td colSpan={3} />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Mark as paid dialog */}
            <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
                <DialogContent className="max-w-md" data-testid="finance-payment-dialog">
                    <DialogHeader>
                        <DialogTitle>{bulkMode ? `Régler ${selectedItems.length} facture(s)` : 'Marquer comme réglé'}</DialogTitle>
                        <DialogDescription>
                            {bulkMode
                                ? `Total sélectionné : ${formatCurrency(selectedTotal)} — la même date et les mêmes notes seront appliquées à toutes les lignes.`
                                : (activeItem ? `${activeItem.supplier_name} · ${activeItem.number} · ${formatCurrency(activeItem.total_amount)}` : '')}
                        </DialogDescription>
                    </DialogHeader>
                    {bulkMode && (
                        <div className="max-h-40 overflow-auto border rounded-lg text-xs" data-testid="finance-bulk-list">
                            {selectedItems.map(it => (
                                <div key={`${it.type}-${it.id}`} className="flex justify-between gap-2 px-3 py-1.5 border-b last:border-b-0">
                                    <span className="truncate">{it.supplier_name} · <span className="font-mono">{it.number}</span></span>
                                    <span className="tabular-nums font-semibold whitespace-nowrap">{formatCurrency(it.total_amount)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="space-y-3 py-2">
                        <div className="space-y-1">
                            <Label className="text-xs">Date de règlement</Label>
                            <Input
                                type="date"
                                value={activePaymentDate}
                                onChange={e => setActivePaymentDate(e.target.value)}
                                data-testid="finance-payment-date-input"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Notes (optionnel)</Label>
                            <Input
                                value={activePaymentNotes}
                                onChange={e => setActivePaymentNotes(e.target.value)}
                                placeholder="Ex: virement SEPA, chèque n°1234..."
                                data-testid="finance-payment-notes-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPaymentDialog(false)}>Annuler</Button>
                        <Button onClick={handleSavePayment} disabled={bulkSaving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="finance-confirm-payment-btn">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> {bulkSaving ? 'Enregistrement…' : (bulkMode ? `Régler ${selectedItems.length} facture(s)` : 'Confirmer le règlement')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SupplierShortcutEditor
                open={showShortcutEditor}
                onClose={() => setShowShortcutEditor(false)}
                suppliers={allSuppliers}
                shortcutNames={supplierShortcutNames}
                onChange={setSupplierShortcutNames}
                defaults={DEFAULT_SUPPLIER_SHORTCUTS}
                resolveShortcutSupplier={resolveShortcutSupplier}
            />
            <ManualReceptionDetailDialog
                receptionId={previewReceptionId}
                onClose={() => setPreviewReceptionId(null)}
            />
            <OrderDetailDialog
                orderId={previewOrderId}
                onClose={() => setPreviewOrderId(null)}
            />
        </div>
    );
}

/**
 * Compact editor dialog for the user-editable supplier shortcut list.
 * Suppliers list is already filtered to en_compte only.
 */
function SupplierShortcutEditor({ open, onClose, suppliers, shortcutNames, onChange, defaults, resolveShortcutSupplier }) {
    const [draft, setDraft] = useState([]);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        if (open) {
            setDraft([...shortcutNames]);
            setNewName('');
        }
    }, [open, shortcutNames]);

    const handleAdd = () => {
        const name = newName.trim();
        if (!name) return;
        if (draft.some(n => n.toLowerCase() === name.toLowerCase())) {
            toast.error('Ce fournisseur est déjà dans les raccourcis');
            return;
        }
        setDraft([...draft, name]);
        setNewName('');
    };

    const handleRemove = (idx) => setDraft(draft.filter((_, i) => i !== idx));
    const handleMove = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= draft.length) return;
        const copy = [...draft];
        [copy[idx], copy[target]] = [copy[target], copy[idx]];
        setDraft(copy);
    };
    const handleSave = () => { onChange(draft); onClose(); };
    const handleReset = () => setDraft([...defaults]);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="finance-shortcut-editor-dialog">
                <DialogHeader>
                    <DialogTitle>Raccourcis fournisseurs</DialogTitle>
                    <DialogDescription>
                        Sélectionnez un fournisseur en compte dans la liste, ou tapez un nom librement.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-auto py-1">
                    {draft.map((name, idx) => {
                        const supplier = resolveShortcutSupplier(name);
                        return (
                            <div key={`${name}-${idx}`} className="flex items-center gap-1 border rounded px-2 py-1">
                                <span className="flex-1 text-sm">
                                    {supplier?.name || name}
                                    {!supplier && <span className="text-xs text-red-500 ml-2">(introuvable / non en compte)</span>}
                                </span>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, -1)} disabled={idx === 0}>↑</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, 1)} disabled={idx === draft.length - 1}>↓</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={() => handleRemove(idx)}>×</Button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-1">
                    <Select value="" onValueChange={(v) => v && setNewName(v)}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Choisir dans les fournisseurs en compte…" /></SelectTrigger>
                        <SelectContent>
                            {suppliers
                                .filter(s => !draft.some(n => resolveShortcutSupplier(n)?.id === s.id))
                                .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
                                .map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)
                            }
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-1">
                    <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ou taper un nom..." className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
                    <Button size="sm" onClick={handleAdd} className="h-8 text-xs">Ajouter</Button>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={handleReset}>Réinitialiser</Button>
                    <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
                    <Button size="sm" onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white">Enregistrer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
